/**
 * Marking layer — turns a question's /10 SCORE into exam MARKS for a test.
 *
 * Distinct from the scoring layer (`scorers/*`, which produces /10 quality):
 * marking is test-context. A question is *worth* `marks` (default
 * `question.marks`, test-overridable); a test applies a per-section
 * `MarkingScheme` (the rule) to award marks. Marks have no meaning without a
 * test. See docs/FeatureDesigns/AssessmentAndMarking/scoring-and-marking.md.
 *
 * This module is pure (no I/O) and shared by the backend roll-up and the admin
 * authoring presets.
 */

import type { CreditMatch } from './creditRubric';

/**
 * Which preset this rule was chosen as.
 *
 * It exists because several presets are the same RULE under different exam
 * names — JEE Main and NEET both mark `standard` at ¼ — so without it the
 * choice cannot be recovered, and a teacher who picked NEET was shown JEE Main
 * on every later visit.
 *
 * It began as presentation only. It is NOT that any more, and the difference
 * decides whether a paper can be handed out at all: `schemeDefaultMarks` reads
 * the tag to supply the WORTH a preset implies (a JEE Main question is 4
 * because that is what the name means), `worthOnPaper` awards on that worth,
 * and `assertEveryQuestionCarriesMarks` refuses to assign a paper where it
 * resolves to nothing. So `{kind:'standard', negativeFraction:0.25}` and the
 * same rule tagged `jee_main` mark an answered question identically and differ
 * on whether there is a paper to answer — an untagged rule on unpriced
 * questions is worth zero, and 63 of the 316 previous-year papers are blocked
 * in exactly that state.
 *
 * Absent on every scheme stored before this and on any set by the system (an
 * uploaded mark scheme is not a preset); readers fall back to matching the
 * rule, which is right to within the name but recovers no worth.
 */
interface PresetTag {
    preset?: string;
}

/**
 * Marks for a question left NOT ATTEMPTED, on the three answer rules — signed:
 * −1 is a penalty, 0 (the default, and every paper before this field) nothing.
 * "Not attempted" is no final answer: left blank, withdrawn, or worked without
 * an answer committed. A page we could not read is never one — the student
 * answered; we failed to read it — and earns nothing either way.
 */
interface BlankMarks {
    blank?: number;
}

/** A marking RULE (no magnitude — the worth comes from the question's marks). */
export type MarkingScheme =
    /** Single-correct: correct → +worth; wrong → −(negativeFraction × worth); blank → `blank` (0). */
    | ({ kind: 'standard'; negativeFraction?: number } & BlankMarks & PresetTag)
    /** MCQ_MULTI proportional: (correctChosen/totalCorrect) × worth; any wrong → −(negativeFraction × worth). */
    | ({ kind: 'multi_partial'; negativeFraction?: number } & BlankMarks & PresetTag)
    /** JEE-Advanced multiple-correct: +perOption per correct option (full = worth
     *  when all correct chosen), any wrong → −negative (flat, canonical −2),
     *  blank → 0. `perOption` defaults to 1 — the canonical paper; a paper that
     *  prints a different per-option credit states it (see paperRulesRead). */
    | ({ kind: 'multi_jee'; negative?: number; perOption?: number } & BlankMarks & PresetTag)
    /** Freeform / structured: (overallScore/10) × worth; never negative. */
    | ({ kind: 'rubric' } & PresetTag)
    /** Award the marks the question's own uploaded mark scheme specifies (M/A/B
     *  credit items) instead of a proportional approximation. The rule lives on
     *  the test; the credit items live on each question (`Question.markingRubric`).
     *  Falls back per question when no rubric is present — see design §7.
     *  See docs/FeatureDesigns/AssessmentAndMarking/marking-scheme-upload.md. */
    | ({ kind: 'credit_rubric' } & PresetTag)
    /** A paper's own "N for working, +A right option, −P wrong option" rule.
     *  Unlike every other kind it CARRIES magnitudes: the paper states the
     *  split, and one worth cannot be divided into it. Working is marked from
     *  the working alone (approach score) so a wrong option keeps its working
     *  marks; the option is all-or-nothing, a blank costing nothing. On a
     *  STRUCTURED question each non-objective part is a working unit worth
     *  `workingMarks`, and the one objective part is the shared final option.
     *  Marks are whole numbers.
     *  See docs/FeatureDesigns/AssessmentAndMarking/paper-stated-marking-target.md. */
    | ({ kind: 'working_answer'; workingMarks: number; answerMarks: number; wrongPenalty: number } & PresetTag);

export type MarkingSchemeKind = MarkingScheme['kind'];

/**
 * Is this paper a TEST — the only kind that carries marks? An assignment
 * (`defaultModality: 'practice'`; legacy `type: 'practice-set'`) has no marks:
 * marking rules printed on a paper imported as an assignment are ignored, never
 * read into a draft, never applied. The teacher app reads the same field.
 * See docs/FeatureDesigns/AssessmentAndMarking/paper-stated-marking-target.md §5.
 */
export function paperTakesMarks(paper: { defaultModality?: string | null; type?: string | null }): boolean {
    if (paper.defaultModality) return paper.defaultModality !== 'practice';
    return paper.type !== 'practice-set';
}

/**
 * Does this paper carry a marking rule at all?
 *
 * Tier 2 puts the rule at two levels — one default on the test, one override per
 * section — so "has a scheme" is a two-place question that both the session
 * engine and the teacher's view of the paper have to ask, and get the same
 * answer to. Stated once here so they cannot drift: a paper marks if EITHER
 * level supplies a rule.
 *
 * Says nothing about whether marks will actually be awarded — that also needs
 * the sitting to score at all, which practice does not (see qSetSessionEngine).
 * This answers only the half that is a property of the paper.
 */
export function hasMarkingRule(paper: {
    marking?: MarkingScheme;
    sections?: readonly { marking?: MarkingScheme }[];
}): boolean {
    return !!(paper.marking || paper.sections?.some(s => s.marking));
}

/**
 * What a question on this paper is worth when neither the paper nor the question
 * says — the magnitude the paper's own marking preset is named for.
 *
 * Read from the preset tag, not from the rule: `standard` at ¼ is JEE Main AND
 * NEET AND a school paper that happens to mark that way, and only the first two
 * mean 4. A scheme with no tag (stored before tags existed, or set by an
 * uploaded mark scheme) supplies nothing, which is correct — we do not know what
 * exam it is.
 *
 * The paper-level rule wins over a section's: sections exist to mark PARTS of a
 * paper differently, and a per-section worth is a question this does not answer.
 * See docs/FeatureDesigns/assignments-tests/per-test-question-marks-target.md.
 */
export function schemeDefaultMarks(paper: {
    marking?: MarkingScheme;
    sections?: readonly { marking?: MarkingScheme }[];
}, type?: string | null): number | undefined {
    const scheme = paper.marking ?? paper.sections?.find(s => s.marking)?.marking;
    // A paper-stated split names its magnitude outright (4 working + 1 option
    // = 5). Flat questions only — a STRUCTURED one prices itself from its parts.
    if (scheme?.kind === 'working_answer') return workingAnswerWorth(scheme);
    const preset = presetOf(scheme);
    if (!preset) return undefined;
    // A preset that marks by question type prices by it too: JEE Advanced is
    // 3 for a single-correct question and 4 for a multiple-correct one.
    const forType = type ? preset.byType?.[type as MarkableType] : undefined;
    return forType ? forType.defaultMarks : preset.defaultMarks;
}

/** Presets renamed since schemes were stored under them: old tag → current key. */
const PRESET_ALIASES: Record<string, string> = {
    // "JEE Advanced · multiple correct" became "JEE Advanced", marked by type.
    jee_adv_multi: 'jee_adv',
};

/** The preset a scheme was chosen as, following renames. Untagged → undefined. */
export function presetKeyOf(scheme: MarkingScheme | undefined): string | undefined {
    const tag = scheme && 'preset' in scheme ? scheme.preset : undefined;
    if (!tag) return undefined;
    const key = PRESET_ALIASES[tag] ?? tag;
    return MARKING_PRESETS[key] ? key : undefined;
}

function presetOf(scheme: MarkingScheme | undefined): MarkingPreset | undefined {
    const key = presetKeyOf(scheme);
    return key ? MARKING_PRESETS[key] : undefined;
}

/** Does this scheme mark each question by its type (a `byType` preset)? */
export function marksByType(scheme: MarkingScheme | undefined): boolean {
    return !!presetOf(scheme)?.byType;
}

/**
 * The rule a question of this type is marked by. A paper is often one rule for
 * every question; an exam like JEE Advanced is not — single-correct, multiple-
 * correct and numerical questions each have their own — and its preset says
 * so per type. Any other scheme is returned as it is.
 */
export function schemeForType(scheme: MarkingScheme | undefined, type: string | null | undefined): MarkingScheme | undefined {
    const forType = type ? presetOf(scheme)?.byType?.[type as MarkableType] : undefined;
    return forType ? forType.scheme : scheme;
}

/** The marking-relevant signal for one question. Persisted on the result by the
 *  scorer; richer than the bare /10 score so the multi rules can run later. */
export interface QuestionMarkingInput {
    /** The student engaged with the question — working, an answer, or both. */
    attempted: boolean;
    /** Every step of the working was crossed out and no answer stands: the
     *  student withdrew the question. Set with `attempted: false`, so it earns
     *  nothing, loses nothing and does not take one of a section's K.
     *  See docs/FeatureDesigns/AssessmentAndMarking/paper-stated-marking-target.md §4. */
    withdrawn?: true;
    /**
     * The student put down a FINAL ANSWER. Narrower than `attempted`, and it is
     * this — not `attempted` — that a mark is owed to or withheld from on an
     * objective question.
     *
     * Worked and never answered is the case the two words exist to separate: an
     * exam gives it zero, and reading `attempted` as commitment gave it the full
     * negative instead. Derived by `deriveCommitment` from the answer of record
     * and whether the script carried an answer sheet.
     *
     * Optional so a result row written before this existed still marks the way
     * it did when it was written — absence falls back to `attempted`, which is
     * exactly the old behaviour, rather than silently re-marking old attempts.
     * See docs/FeatureDesigns/AssessmentAndMarking/scoring-and-marking.md.
     */
    committed?: boolean;
    /**
     * No answer could be read off the page, so there is no answer of record to
     * mark. `correctness` is meaningless on such a row — it is derived from a
     * score that was never computed — and under a negative scheme reading it as
     * `incorrect` does not merely withhold the mark, it DEDUCTS one. A student
     * loses marks for our failure to read their page.
     *
     * Marked like an uncommitted answer: nothing awarded, nothing deducted. The
     * result row carries `status: 'unread'` so a teacher can read the page and
     * award what it deserves.
     * See docs/FeatureDesigns/solution-analysis/unread-answer-target.md.
     */
    unread?: true;
    /** Correctness band — drives `standard`. */
    correctness: 'correct' | 'partial' | 'incorrect';
    /** MCQ_MULTI only — option-level counts (`evaluateMultiSelect` already has these). */
    multi?: { totalCorrect: number; correctChosen: number; wrongChosen: number };
    /** /10 overall (finalScore) — drives `rubric`. */
    overallScore?: number;
    /** Credit-rubric marking: which of the question's mark-scheme credit
     *  conditions the analysis model found in the student's working, with the
     *  evidence for each. Drives `credit_rubric`; ignored by every other kind.
     *  See docs/FeatureDesigns/AssessmentAndMarking/marking-scheme-upload.md. */
    credits?: CreditMatch[];
    /** Credit-rubric marking: did the student's FINAL answer match the MARK
     *  SCHEME's accepted answers? Distinct from `correctness`, which is judged
     *  against the question's own `correctAnswer` by the /10 scorer — for a paper
     *  that shipped with a scheme, the scheme is the authority and the two can
     *  legitimately disagree. Set by one-shot marking, where the model saw the
     *  scheme and the attempt in the same call. Absent ⇒ the credit path falls
     *  back to `correctness === 'correct'`.
     *  See docs/FeatureDesigns/AssessmentAndMarking/one-shot-marking.md. */
    answerCorrect?: boolean;
    /** /10 quality of the WORKING alone (`computedScores.approachScore`) —
     *  drives `working_answer`. Absent ⇒ no working was found. */
    approachScore?: number;
    /** The approach score was withheld because the page may have been misread
     *  (analysis trust). Working marks are then left for the teacher rather
     *  than awarded as zero. */
    workingWithheld?: true;
    /** STRUCTURED only — one row per part, for `working_answer`. */
    parts?: MarkingPartInput[];
}

/** A STRUCTURED part as `working_answer` sees it. */
export interface MarkingPartInput {
    partId: string;
    /** `answer` = the objective part carrying the shared final option;
     *  `working` = everything else. */
    role: 'working' | 'answer';
    attempted: boolean;
    /** /10 overall for the part — a working part's marks. */
    score?: number;
    /** Band of the part's own answer — the answer part's ±. */
    correctness?: 'correct' | 'partial' | 'incorrect';
}

export interface MarkingResult {
    marksAwarded: number;
    maxMarks: number;
    negativeApplied: boolean;
    /** `working_answer` only: where the marks came from. */
    breakdown?: MarksBreakdown;
    /** Marks the system could not award and left for a teacher — e.g.
     *  `'working'` when the working's score was withheld on low trust. */
    manualPending?: string[];
}

export interface MarksBreakdown {
    working: number;
    workingMax: number;
    /** +answerMarks, −wrongPenalty, or 0 (blank / not right-or-wrong). */
    answer: number;
    answerMax: number;
    /** Did the student commit a final option at all? */
    answerCommitted: boolean;
    /** STRUCTURED: per working part. */
    parts?: Array<{ partId: string; marks: number; maxMarks: number }>;
}

/** Question types the marking layer recognizes. */
export type MarkableType = 'MCQ' | 'MCQ_MULTI' | 'NUMERICAL' | 'SINGLELINE' | 'FREEFORM' | 'STRUCTURED';

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** What a rule awards a question left not attempted — 0 unless it says otherwise. */
export function blankMarksOf(scheme: MarkingScheme | undefined): number {
    if (!scheme) return 0;
    if (scheme.kind === 'standard' || scheme.kind === 'multi_partial' || scheme.kind === 'multi_jee') {
        return scheme.blank ?? 0;
    }
    return 0;
}

/** Default scheme kind for a type when the section declares none. */
function defaultKind(type: MarkableType): MarkingSchemeKind {
    if (type === 'FREEFORM' || type === 'STRUCTURED') return 'rubric';
    if (type === 'MCQ_MULTI') return 'multi_partial';
    return 'standard';
}

/**
 * Award marks for one question. `worth` is the resolved magnitude
 * (override ?? question.marks). Subjective types (FREEFORM/STRUCTURED) always
 * use the rubric path regardless of the section scheme — you can't all-or-nothing
 * an essay.
 */
export function applyMarking(
    type: MarkableType,
    input: QuestionMarkingInput,
    scheme: MarkingScheme | undefined,
    worth: number,
): MarkingResult {
    // A by-type preset (JEE Advanced) hands each type its own rule. Without
    // this, its multiple-correct rule marked a single-correct MCQ or a
    // numerical from option counts it never has — zero, right or wrong.
    scheme = schemeForType(scheme, type);
    const maxMarks = worth;
    const none = (): MarkingResult => ({ marksAwarded: 0, maxMarks, negativeApplied: false });
    // Not attempted: what the rule says for a blank (0 unless it says
    // otherwise). Subjective types and the rubric kinds have no such rule.
    const blank = (): MarkingResult => {
        const b = type === 'FREEFORM' || type === 'STRUCTURED' ? 0 : blankMarksOf(scheme);
        return { marksAwarded: round2(b), maxMarks, negativeApplied: b < 0 };
    };

    if (scheme?.kind === 'working_answer') return applyWorkingAnswer(type, input, scheme);

    if (worth <= 0) return none();

    // Before any scheme runs: an unread page has no answer of record, so every
    // rule below would be judging a `correctness` band derived from a score
    // that was never computed. Same posture as an uncommitted answer — see the
    // `committed` rule further down — but checked first, because the multi and
    // rubric paths return before reaching it. Never the blank rule either: the
    // student answered; we could not read it.
    if (input.unread) return none();

    if (!input.attempted) return blank();

    // Subjective types ignore the scheme kind — always continuous, never negative.
    if (type === 'FREEFORM' || type === 'STRUCTURED') {
        return { marksAwarded: round2(((input.overallScore ?? 0) / 10) * worth), maxMarks, negativeApplied: false };
    }

    const kind: MarkingSchemeKind = scheme?.kind ?? defaultKind(type);

    if (kind === 'rubric') {
        return { marksAwarded: round2(((input.overallScore ?? 0) / 10) * worth), maxMarks, negativeApplied: false };
    }

    if (kind === 'multi_jee') {
        const m = input.multi;
        const negative = (scheme?.kind === 'multi_jee' ? scheme.negative : undefined) ?? 2;
        if (!m || (m.correctChosen + m.wrongChosen) === 0) return blank();
        if (m.wrongChosen > 0) return { marksAwarded: -round2(negative), maxMarks, negativeApplied: true };
        // No wrong: full marks when every correct option chosen, else
        // +perOption per correct — never more than the question is worth.
        const perOption = (scheme?.kind === 'multi_jee' ? scheme.perOption : undefined) ?? 1;
        const awarded = m.correctChosen >= m.totalCorrect ? worth : Math.min(worth, m.correctChosen * perOption);
        return { marksAwarded: round2(awarded), maxMarks, negativeApplied: false };
    }

    if (kind === 'multi_partial') {
        const m = input.multi;
        const negFrac = (scheme?.kind === 'multi_partial' ? scheme.negativeFraction : undefined) ?? 0;
        if (m) {
            if (m.wrongChosen > 0) {
                return negFrac > 0
                    ? { marksAwarded: -round2(negFrac * worth), maxMarks, negativeApplied: true }
                    : { marksAwarded: 0, maxMarks, negativeApplied: false };
            }
            const frac = m.totalCorrect > 0 ? m.correctChosen / m.totalCorrect : 0;
            return { marksAwarded: round2(frac * worth), maxMarks, negativeApplied: false };
        }
        // No option detail — fall back to the correctness band.
    }

    // No final answer, no mark — in either direction. An exam does not penalise a
    // blank, and it does not reward one either: working that reaches the key with
    // nothing written down scores 10/10 on quality and earns nothing here, which
    // is precisely the "marks left on the table" the report measures. The multi
    // rules above already test this in disguise (`chosen == 0 → 0`, and a
    // negative needs `wrongChosen > 0`); `standard` is the rule that did not, and
    // it is the one every JEE/NEET preset uses.
    if (!(input.committed ?? input.attempted)) return blank();

    // standard (and multi_partial fallback): correct → +worth; incorrect → −(frac × worth); partial → 0.
    const negFrac = (scheme && (scheme.kind === 'standard' || scheme.kind === 'multi_partial') ? scheme.negativeFraction : undefined) ?? 0;
    if (input.correctness === 'correct') return { marksAwarded: round2(worth), maxMarks, negativeApplied: false };
    if (input.correctness === 'incorrect') {
        return negFrac > 0
            ? { marksAwarded: -round2(negFrac * worth), maxMarks, negativeApplied: true }
            : { marksAwarded: 0, maxMarks, negativeApplied: false };
    }
    return { marksAwarded: 0, maxMarks, negativeApplied: false }; // partial under all-or-nothing → no marks, no penalty
}

/** Whole marks: the paper's own unit. Half up. */
const wholeMarks = (score: number | undefined, outOf: number): number =>
    Math.min(outOf, Math.max(0, Math.round(((score ?? 0) / 10) * outOf)));

/** What a question is worth under `working_answer` — the scheme's own split,
 *  times the working units (one for a flat question, one per working part). */
export function workingAnswerWorth(
    scheme: Extract<MarkingScheme, { kind: 'working_answer' }>,
    workingUnits = 1,
): number {
    return scheme.workingMarks * workingUnits + scheme.answerMarks;
}

/**
 * Which role each part of a STRUCTURED question plays under `working_answer`:
 * one part carries the shared final option, every other part is working.
 *
 * Decided from the question's shape, not from each part's own type: a working
 * part that asks for a value ("(i) find x") is imported as NUMERICAL, so a
 * type test would read it as a second answer. The final-option part is the one
 * extraction names `final`; failing that, the only MCQ part; failing that, the
 * last part when it is objective. No such part ⇒ every part is working.
 */
export function markingPartRoles(
    parts: ReadonlyArray<{ type?: string; partId?: string }>,
): MarkingPartInput['role'][] {
    const isOption = (t?: string) => t === 'MCQ' || t === 'MCQ_MULTI';
    const isObjective = (t?: string) => isOption(t) || t === 'NUMERICAL' || t === 'SINGLELINE';
    let answer = parts.findIndex(p => p.partId?.trim().toLowerCase() === 'final');
    if (answer < 0) {
        const options = parts.flatMap((p, i) => isOption(p.type) ? [i] : []);
        if (options.length === 1) answer = options[0];
    }
    if (answer < 0 && parts.length > 1 && isObjective(parts[parts.length - 1].type)) answer = parts.length - 1;
    return parts.map((_, i) => i === answer ? 'answer' : 'working');
}

/** A question's worth under `working_answer`: a flat question is one working
 *  unit; a STRUCTURED one has a unit per working part. */
export function workingAnswerWorthFor(
    scheme: Extract<MarkingScheme, { kind: 'working_answer' }>,
    question: { questionType?: string | null; parts?: ReadonlyArray<{ type?: string; partId?: string }> },
): number {
    const units = question.questionType === 'STRUCTURED' && question.parts?.length
        ? markingPartRoles(question.parts).filter(r => r === 'working').length
        : 1;
    return workingAnswerWorth(scheme, units);
}

/**
 * `working_answer`: working marks from the working alone, plus ± on the final
 * option. The working is marked whether the option is right, wrong or blank —
 * the paper says so. The worth is the scheme's, not the caller's: the rule
 * states its magnitudes.
 */
function applyWorkingAnswer(
    type: MarkableType,
    input: QuestionMarkingInput,
    scheme: Extract<MarkingScheme, { kind: 'working_answer' }>,
): MarkingResult {
    const { workingMarks, answerMarks, wrongPenalty } = scheme;
    const structuredParts = type === 'STRUCTURED' ? input.parts : undefined;
    const workingParts = structuredParts?.filter(p => p.role === 'working');
    const workingUnits = workingParts ? workingParts.length : 1;
    const workingMax = workingMarks * workingUnits;
    const maxMarks = workingMax + answerMarks;
    const zero = (): MarkingResult => ({ marksAwarded: 0, maxMarks, negativeApplied: false });

    if (!input.attempted || input.unread) return zero();

    // ── Working ──
    let working = 0;
    let parts: MarksBreakdown['parts'];
    const manualPending: string[] = [];
    if (workingParts) {
        parts = workingParts.map(p => ({
            partId: p.partId,
            marks: p.attempted ? wholeMarks(p.score, workingMarks) : 0,
            maxMarks: workingMarks,
        }));
        working = parts.reduce((sum, p) => sum + p.marks, 0);
    } else if (input.workingWithheld) {
        manualPending.push('working');
    } else {
        working = wholeMarks(input.approachScore, workingMarks);
    }

    // ── Final option ──
    let committed: boolean;
    let correctness: QuestionMarkingInput['correctness'] | undefined;
    if (structuredParts) {
        const answerPart = structuredParts.find(p => p.role === 'answer');
        committed = !!answerPart?.attempted;
        correctness = answerPart?.correctness;
    } else {
        committed = input.committed ?? input.attempted;
        correctness = input.correctness;
    }
    const answer = !committed ? 0
        : correctness === 'correct' ? answerMarks
        : correctness === 'incorrect' ? -wrongPenalty
        : 0;

    return {
        marksAwarded: working + answer,
        maxMarks,
        negativeApplied: answer < 0,
        breakdown: { working, workingMax, answer, answerMax: answerMarks, answerCommitted: committed, ...(parts ? { parts } : {}) },
        ...(manualPending.length ? { manualPending } : {}),
    };
}

/**
 * The rule in plain words, for every surface that shows a teacher what marking
 * is applied — the paper's Marking panel, the editor and the results — so all
 * three say the same thing. `structured` phrases `working_answer` per part.
 */
export function describeMarking(scheme: MarkingScheme | undefined, opts: { structured?: boolean; worth?: number } = {}): string {
    if (!scheme) return 'No marks — scored out of 10 only';
    const byType = presetOf(scheme)?.byType;
    if (byType) {
        return (Object.entries(byType) as [MarkableType, NonNullable<MarkingPreset['byType']>[MarkableType]][])
            .map(([t, r]) => `${TYPE_WORDS[t]}: ${describeMarking(r!.scheme, { worth: r!.defaultMarks })}`)
            .join('. ');
    }
    const sign = (n: number) => (n === 0 ? '0' : `−${n}`);
    // With the question's worth known, the rule is said in marks ("+3 if right ·
    // −1 if wrong"), the way the paper prints it, not as fractions of "full marks".
    const w = opts.worth && opts.worth > 0 ? opts.worth : undefined;
    const m = (v: number) => String(Math.round(v * 100) / 100);
    switch (scheme.kind) {
        case 'standard': {
            const f = scheme.negativeFraction ?? 0;
            const b = scheme.blank ?? 0;
            if (w) {
                if (b !== 0) return `+${m(w)} if right · ${f > 0 ? `−${m(f * w)}` : '0'} if wrong · ${blankWord(b)} if blank`;
                return f > 0 ? `+${m(w)} if right · −${m(f * w)} if wrong · 0 if blank` : `+${m(w)} if right · 0 if wrong or blank`;
            }
            if (b !== 0) return `Full marks if right · ${f > 0 ? `−${fraction(f)} of the marks` : '0'} if wrong · ${blankWord(b)} if blank`;
            return f > 0
                ? `Full marks if right · −${fraction(f)} of the marks if wrong · 0 if blank`
                : 'Full marks if right · 0 if wrong or blank';
        }
        case 'multi_partial': {
            const f = scheme.negativeFraction ?? 0;
            const wrong = f > 0 ? (w ? `−${m(f * w)}` : `−${fraction(f)} of the marks`) : '0';
            const b = scheme.blank ?? 0;
            return `${w ? `Up to +${m(w)}, in` : 'Marks in'} proportion to the right options chosen · ${wrong} if any wrong option${b !== 0 ? ` · ${blankWord(b)} if blank` : ''}`;
        }
        case 'multi_jee': {
            const b = blankWord(scheme.blank ?? 0);
            return w
                ? `+${m(w)} if all right options · +${m(scheme.perOption ?? 1)} per right option if no wrong one · ${sign(scheme.negative ?? 2)} if any wrong option · ${b} if blank`
                : `+${scheme.perOption ?? 1} per right option (full marks for all) · ${sign(scheme.negative ?? 2)} if any wrong option · ${b} if blank`;
        }
        case 'rubric':
            return `${w ? `Up to ${m(w)} marks, in` : 'Marks in'} proportion to the score out of 10 · never negative`;
        case 'credit_rubric':
            return "Marks from the paper's own mark scheme (method and accuracy marks)";
        case 'working_answer': {
            const working = opts.structured ? `Each part: ${scheme.workingMarks} for working` : `${scheme.workingMarks} for working`;
            const option = opts.structured ? 'final option' : 'option';
            return `${working} · ${option} +${scheme.answerMarks} if right · ${sign(scheme.wrongPenalty)} if wrong · 0 if blank`;
        }
    }
}

/** A question type as a paper's instructions name it. */
const TYPE_WORDS: Record<MarkableType, string> = {
    MCQ: 'Single correct', MCQ_MULTI: 'Multiple correct', NUMERICAL: 'Numerical',
    SINGLELINE: 'Short answer', FREEFORM: 'Long answer', STRUCTURED: 'Multi-part',
};

/** A blank's marks, signed the way a paper prints them: "0", "−1", "+1". */
function blankWord(b: number): string {
    const v = Math.round(Math.abs(b) * 100) / 100;
    return b === 0 ? '0' : b < 0 ? `−${v}` : `+${v}`;
}

function fraction(f: number): string {
    const known: Record<string, string> = { '0.25': '¼', '0.5': '½', '0.2': '⅕', '0.333': '⅓', '1': 'all' };
    return known[String(Math.round(f * 1000) / 1000)] ?? `${Math.round(f * 100)}%`;
}

// ---------------------------------------------------------------------------
// Preset library — named schemes for the standard exams (admin authoring).
// Presets are per section/scheme (one exam spans several section-schemes).
// ---------------------------------------------------------------------------

export interface MarkingPreset {
    label: string;
    /** One-line, student-relatable explanation of how marks are awarded under
     *  this scheme. Surfaced in the authoring UI (the Marking info tooltip). */
    description: string;
    scheme: MarkingScheme;
    /**
     * What a question is worth under this preset, when nothing else says.
     *
     * The RULE has no magnitude — see `MarkingScheme` — but some presets are
     * named for one. "JEE Main · +4 / −1" is not a rule plus a number a teacher
     * still has to supply; the 4 IS what the exam means, and a JEE paper whose
     * questions carry no marks is worth nothing and marks every answer zero.
     * So the preset that names a magnitude supplies it, and a paper carrying
     * that preset prices its questions without anyone typing 4 twelve times.
     *
     * Absent where the exam does not fix one: a boards paper is 1, 2, 3 or 5
     * marks a question and only the paper knows which.
     */
    defaultMarks?: number;
    /**
     * An exam whose paper marks each question TYPE by its own rule and worth —
     * JEE Advanced: single-correct +3 / −1, multiple-correct +4 with +1 per
     * right option / −2, numerical +4 / 0. The stored scheme still carries
     * only this preset's tag; `schemeForType` and `schemeDefaultMarks` read the
     * rule and worth for each question from here. A type not listed falls back
     * to `scheme` and `defaultMarks`. A paper whose sections differ from the
     * exam's usual pattern sets those sections' rules itself.
     */
    byType?: Partial<Record<MarkableType, { scheme: MarkingScheme; defaultMarks?: number }>>;
}

// Each `scheme` carries its own key, so choosing a preset stores WHICH preset —
// see `PresetTag`. Two entries here mark identically on purpose (JEE Main and
// NEET; JEE Main Section B and no-negative): the exam name is what the teacher
// recognises, and the tag is what keeps the two apart.
export const MARKING_PRESETS: Record<string, MarkingPreset> = {
    jee_main:      { label: 'JEE Main · +4 / −1',               description: 'Correct → full marks; wrong → −¼ of the marks; blank → 0.', scheme: { kind: 'standard', negativeFraction: 0.25, preset: 'jee_main' }, defaultMarks: 4 },
    neet:          { label: 'NEET · +4 / −1',                   description: 'Correct → full marks; wrong → −¼ of the marks; blank → 0.', scheme: { kind: 'standard', negativeFraction: 0.25, preset: 'neet' }, defaultMarks: 4 },
    jee_main_num:  { label: 'JEE Main Section B (numerical)',   description: 'Correct → full marks; wrong or blank → 0. No negative marking.', scheme: { kind: 'standard', negativeFraction: 0, preset: 'jee_main_num' }, defaultMarks: 4 },
    jee_adv:       {
        label: 'JEE Advanced',
        description: 'By question type — single correct: +3 / −1; multiple correct: +4 for all right options, +1 per right option if no wrong one, −2 if any wrong; numerical: +4 / 0. Blank → 0.',
        // The fallback for a type the exam does not set: no negative.
        scheme: { kind: 'standard', negativeFraction: 0, preset: 'jee_adv' },
        defaultMarks: 4,
        // Untagged: each is a plain rule — the tag on the stored scheme is what
        // says "by type", and a tagged rule here would resolve to itself again.
        byType: {
            MCQ:       { scheme: { kind: 'standard', negativeFraction: 1 / 3 }, defaultMarks: 3 },
            MCQ_MULTI: { scheme: { kind: 'multi_jee', negative: 2 }, defaultMarks: 4 },
            NUMERICAL: { scheme: { kind: 'standard', negativeFraction: 0 }, defaultMarks: 4 },
        },
    },
    no_negative:   { label: 'No negative marking',              description: 'Correct → full marks; wrong or blank → 0. No penalty (all-or-nothing on objective questions).', scheme: { kind: 'standard', negativeFraction: 0, preset: 'no_negative' } },
    boards:        { label: 'Boards / rubric (no negative)',    description: 'Marks = (score ÷ 10) × the question’s marks — partial credit on every question, never negative.', scheme: { kind: 'rubric', preset: 'boards' } },
};

// NOTE: no preset for `credit_rubric` yet — deliberately. `MARKING_PRESETS` is
// rendered straight into the test editor's dropdown, and until the marking
// integration lands (marking-scheme-upload-plan.md Phase 4) selecting it would
// silently fall back to answer-only marking while promising credit marking. The
// preset is added in Phase 5, alongside the UI that can honour it.
